import { AnomalyAnalyzerService } from '@ids-services';
import { Component, computed, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { THREAT_STATUSES, THREAT_STATUS_OPTIONS, THREAT_TYPES, THREAT_TYPE_DESCRIPTIONS } from '@ids-constants';
import { BaseComponent } from '@ids-components';
import { TargetDeviceService, ThreatService, AssessmentService, ConnectionService } from '@ids-services';
import { verticalSlideAnimation, fadeAnimation } from '@microsec/animations';
import { MODULES } from '../../../../modules';
import { ORGANIZATION_ID_PARAM_ROUTE, PROJECT_ID_PARAM_ROUTE } from '@microsec/constants';
import { PROJECT_MANAGEMENT_CONSTANTS } from '@ids-constants';
import { DEVICES_CONSTANTS } from '../../../../project-management/identify-manage/devices/devices.config';
import { NETWORK_SETTINGS_CONSTANTS } from '../../../../project-management/configuration/network-settings/network-settings.config';
import { ANOMALY_ANALYZER_CONSTANTS } from '../../../../project-management/configuration/anomaly-analyzer/anomaly-analyzer.config';
import { ASSESSMENT_TOOL_CONSTANTS } from '../../../../project-management/evaluate/assessment-tool/assessment-tool.config';

import { Observable, finalize, forkJoin } from 'rxjs';
import { ConstantPipe, MomentPipe, ThreatScorePipe, ThreatTitlePipe } from '@ids-pipes';
import { CardComponent, LoadingOverlayComponent, ScoreScaleComponent } from '@microsec/components';
import { CommonModule } from '@angular/common';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-threat-overview',
  templateUrl: './threat-overview.component.html',
  styleUrls: ['./threat-overview.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ChipModule,
    SelectModule,
    TooltipModule,
    DividerModule,
    SelectButtonModule,
    LoadingOverlayComponent,
    CardComponent,
    ScoreScaleComponent,
    ThreatTitlePipe,
    MomentPipe,
    ThreatScorePipe,
    ConstantPipe,
  ],
  providers: [ThreatTitlePipe, ThreatScorePipe, ConstantPipe],
  animations: [fadeAnimation, verticalSlideAnimation],
})
export class ThreatOverviewComponent extends BaseComponent implements OnInit {
  isLoading = false;

  _threat = {};

  get threat() {
    return this._threat;
  }

  @Input() set threat(value: any) {
    value.updatableStatus = value.status;
    value.updatableIsTruePositive = value.is_true_positive;
    this._threat = value || {};
    if (!!value) {
      let parsedPayload: any = null;
      try {
        let payload = (value.details?.payload as string) || '';
        payload = payload.replace(/\n/g, '<br/>');
        payload = payload.replace(/\\n/g, '<br/>');
        parsedPayload = JSON.parse(payload);
      } catch (e) {
        parsedPayload = null;
      }
      this.threat.updatableStatus = value.status;
      if (!!parsedPayload) {
        this.prettyPayload.set(!!Array.isArray(parsedPayload) ? parsedPayload : [parsedPayload]);
      } else {
        this.prettyPayload.set([]);
      }
      this.threat.descriptionPreview = this.util.parseMarkdown(value?.description || '');
      if (value.threat_type === THREAT_TYPES.NON_COMPLIANCE) {
        this.getAssessments();
        this.getAssessmentTypes();
      }
    }
  }

  prettyPayload: WritableSignal<any[]> = signal([]);

  parsedPrettyPayload = computed(() => {
    const results: any[] = [];
    this.prettyPayload().forEach((payload) => {
      results.push(...Object.entries(payload).map(([key, value]: [string, any]) => ({ label: key, value })));
    });
    return results;
  });

  THREAT_TYPES = THREAT_TYPES;

  THREAT_STATUSES = THREAT_STATUSES;

  threatStatusOptions = this.util.cloneObjectArray(THREAT_STATUS_OPTIONS);

  THREAT_TYPE_DESCRIPTIONS = THREAT_TYPE_DESCRIPTIONS;

  showThreatScoreDetails = false;

  showImpactedDeviceDetails = false;

  showAttackDetails = false;

  payloadViewOptions = [
    { icon: 'fa fa-align-justify', value: 'string' },
    { icon: 'fa fa-table-list', value: 'list' },
  ];

  selectedPayloadView = 'string';

  constructor(
    private threatSrv: ThreatService,
    private targetDeviceSrv: TargetDeviceService,
    private connectionService: ConnectionService,
    private anomalyAnalyzerService: AnomalyAnalyzerService,
    private assessmentSrv: AssessmentService,
  ) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
  }

  updateThreat(id: any, payload: any, successMessage: string, isRefresh = false) {
    this.isLoading = true;
    this.threatSrv
      .updateThreat(id, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage(successMessage);
          this.threatSrv.refresh$.next(isRefresh);
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  getAssessments() {
    if (!!this.threat) {
      this.isLoading = true;
      const requests: Observable<any>[] = ((this.threat.assessment_ids as any[]) || []).map((id) =>
        this.assessmentSrv.getAssessment(id).pipe(this.handleRequest().bind(this)),
      );
      forkJoin(requests)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (rs) => {
            this.threat.assessments = ((this.threat.assessment_ids as any[]) || []).map((id, index) => ({
              id: id,
              name: `Assessment ID ${id}`,
              ...(rs[index] || {}),
            }));
          },
          error: (err) => {
            this.showErrorMessage(err);
          },
        });
    }
  }

  getAssessmentTypes() {
    if (!!this.threat) {
      this.isLoading = true;
      const requests: Observable<any>[] = ((this.threat.assessment_type_ids as any[]) || []).map((id) =>
        this.assessmentSrv.getAssessmentType(id).pipe(this.handleRequest().bind(this)),
      );
      forkJoin(requests)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (rs) => {
            this.threat.assessmentTypes = ((this.threat.assessment_type_ids as any[]) || []).map((id, index) => ({
              id: id,
              name: `Assessment Type ID ${id}`,
              ...(rs[index] || {}),
            }));
          },
          error: (err) => {
            this.showErrorMessage(err);
          },
        });
    }
  }

  openUpdateStatusConfirmation(statusOption: any) {
    const toStatusStr = ` to '${statusOption.label}'`;
    this.confirm({
      action: 'Update',
      objectName: 'Threat',
      customContent: `Are you sure you want to update the following threat${toStatusStr}?<ul><li>Threat ID: ${this.threat.threat_id}</li></ul>${
        statusOption.value === 'Closed' ? `<br/>Closed threats can be viewed by selecting Filter > Status and choosing '${statusOption.label}'` : ''
      }`,
      next: () => {
        const payload = {
          status: statusOption.dataValue,
          is_true_positive: statusOption.isTruePositive,
        };
        this.updateThreat(this.threat.threat_id, payload, `Updated status threat ID: ${this.threat.threat_id} successfully`, true);
      },
      rejectCallback: () => {
        this.threat.updatableStatus = this.threat.status;
        this.threat.updatableIsTruePositive = this.threat.is_true_positive;
      },
    });
  }

  toggleShowThreatScoreDetails() {
    this.showThreatScoreDetails = !this.showThreatScoreDetails;
  }

  toggleShowImpactedDeviceDetails() {
    this.showImpactedDeviceDetails = !this.showImpactedDeviceDetails;
  }

  toggleShowAttackDetails() {
    this.showAttackDetails = !this.showAttackDetails;
  }

  goToDevicePage(device: any) {
    this.targetDeviceSrv.selected = !!device ? { ...device, id: device.device_id } : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.DEVICES.ROUTE}/${DEVICES_CONSTANTS.DETECTED.ROUTE}`,
    );
  }

  goToAssessmentPage(assessment: any) {
    this.assessmentSrv.selected = !!assessment ? assessment : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.ASSESSMENT_TOOL.ROUTE}/${ASSESSMENT_TOOL_CONSTANTS.ASSESSMENTS.ROUTE}`,
    );
  }

  goToAssessmentTypePage(assessmentType: any) {
    this.assessmentSrv.selected = !!assessmentType ? assessmentType : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.ASSESSMENT_TOOL.ROUTE}/${ASSESSMENT_TOOL_CONSTANTS.ASSESSMENT_TYPES.ROUTE}`,
    );
  }

  goToConnectionPage(connection_id: any) {
    this.connectionService.selected = !!connection_id ? { id: connection_id } : null;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.NETWORK_SETTINGS.ROUTE}/${NETWORK_SETTINGS_CONSTANTS.NETWORKS.ROUTE}`,
    );
  }

  goToAnalyzerPage(analyzer_id: any, isRuleBased: boolean, isSnort = false) {
    this.anomalyAnalyzerService.selected = !!analyzer_id ? { id: analyzer_id, isSnort: isSnort, customRuleId: this.threat?.details?.id } : {};
    const analyzerRoute = `${!!isRuleBased ? ANOMALY_ANALYZER_CONSTANTS.RULE_BASED.ROUTE : ANOMALY_ANALYZER_CONSTANTS.MACHINE_LEARNING.ROUTE}`;
    this.changeRoute(
      `${MODULES.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.ANOMALY_ANALYZER.ROUTE}/${analyzerRoute}`,
    );
  }

  openSnortUrl(snortRuleId: string) {
    const param: any[] = snortRuleId.split(':') || [];
    if (parseInt(param[0]) === 1 && parseInt(param[1]) >= 1000000) {
      this.goToAnalyzerPage(this.threat.analyzer_id, true, true);
    } else {
      this.openUrl('https://www.snort.org/rule_docs/' + param.slice(0, 2).join('-'));
    }
  }

  openUrl(url: any) {
    window.open(url, '_blank');
  }
}
